import { useMemo, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import api, { apiError } from "../../lib/api";
import { CheckCircle2, AlertTriangle, Loader2 } from "lucide-react";

const MARKETS = ["United Arab Emirates", "GCC", "Europe", "Asia", "Africa", "Americas", "Global"];
const REQUIREMENTS = ["Under 10 vehicles / month", "10–50 vehicles / month", "50–200 vehicles / month", "200+ vehicles / month", "Not sure yet"];
const POLICY_VERSION = "v1";

const getUtm = (search) => {
  const p = new URLSearchParams(search);
  const utm = {};
  ["source", "medium", "campaign", "content", "term"].forEach((k) => {
    const v = p.get(`utm_${k}`);
    if (v) utm[k] = v;
  });
  return utm;
};

export const PartnerEnquiryForm = ({ category }) => {
  const location = useLocation();
  const mountedAt = useRef(Date.now());
  const [values, setValues] = useState({ name: "", email: "", phone: "+971 ", company: "", market: "", monthly_requirement: "", message: "", consent: false });
  const [qual, setQual] = useState({});
  const [errors, setErrors] = useState({});
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [failed, setFailed] = useState(false);
  const utm = useMemo(() => getUtm(location.search), [location.search]);

  const set = (k) => (e) => setValues({ ...values, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });

  const validate = () => {
    const er = {};
    if (!values.name.trim()) er.name = "Please tell us your name";
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(values.email)) er.email = "Enter a valid email address";
    if (values.phone.replace(/\D/g, "").length < 9) er.phone = "Enter a valid phone number";
    if (!values.company.trim()) er.company = "Company or organisation is required";
    if (!values.market) er.market = "Choose your primary market";
    if (!values.monthly_requirement) er.monthly_requirement = "Choose your expected volume";
    const len = values.message.trim().length;
    if (len < 20) er.message = "Tell us a little more — at least 20 characters";
    if (len > 1500) er.message = "Please keep it under 1,500 characters";
    if (!values.consent) er.consent = "Please accept the privacy consent to continue";
    category.qualification.forEach((f) => {
      if (!String(qual[f.name] || "").trim()) er[f.name] = "This field helps us route your enquiry";
    });
    setErrors(er);
    return Object.keys(er).length === 0;
  };

  const submit = async (e) => {
    e.preventDefault();
    setFailed(false);
    if (!validate()) return;
    setBusy(true);
    try {
      const r = await api.post("/partner-enquiries", {
        category: category.slug,
        name: values.name.trim(), email: values.email.trim(), phone: values.phone.trim(),
        company: values.company.trim(), market: values.market, monthly_requirement: values.monthly_requirement,
        message: values.message.trim(),
        qualification: qual,
        consent: values.consent, policy_version: POLICY_VERSION,
        landing_page: location.pathname, utm,
        referrer: document.referrer || "",
        device_type: window.matchMedia("(max-width: 768px)").matches ? "mobile" : "desktop",
        form_time_seconds: Math.round((Date.now() - mountedAt.current) / 1000),
      });
      setResult(r.data);
    } catch (err) {
      setFailed(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  if (result)
    return (
      <div className="bg-[#f0f9f0] border border-[#009900]/25 rounded-2xl p-8 text-center" data-testid="partner-form-success">
        <CheckCircle2 className="w-10 h-10 text-[#009900] mx-auto mb-3" />
        <h3 className="heading-upper text-lg text-[#144733]">Thank you — we're on it.</h3>
        <p className="text-sm text-[#41504a] mt-2">Your enquiry <strong>{result.enquiry_id}</strong> is with our <strong>{result.owner}</strong>, who will respond within <strong>{result.sla_hours} business hours</strong>. An acknowledgement email is on its way (demo — logged, not sent).</p>
        <div className="flex justify-center gap-3 mt-6">
          <Link to="/fleet" className="btn-cta rounded px-5 py-2.5 text-xs" data-testid="partner-success-fleet">Explore the fleet</Link>
          <Link to="/partner-with-us" className="border border-[#d5ddd5] rounded px-5 py-2.5 text-xs font-semibold text-[#144733] bg-white" data-testid="partner-success-return">Return to partnerships</Link>
        </div>
      </div>
    );

  const err = (k) => errors[k] && <p className="text-[11px] text-red-600 mt-1" data-testid={`partner-error-${k}`}>{errors[k]}</p>;
  const inputCls = (k) => `field-input ${errors[k] ? "!border-red-400" : ""}`;

  return (
    <form onSubmit={submit} className="bg-white border border-[#E2E8F0] rounded-2xl p-6 space-y-4" noValidate data-testid="partner-enquiry-form">
      <div className="flex items-center justify-between bg-[#fafcfa] border border-dashed border-[#e2ebe4] rounded p-3">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-[#8a978f]">Partnership type</div>
          <div className="text-sm font-bold text-[#144733]" data-testid="partner-form-category">{category.label}</div>
        </div>
        <Link to="/partner-with-us#categories" className="text-xs text-[#009900] underline" data-testid="partner-form-change">Change</Link>
      </div>
      <div className="grid sm:grid-cols-2 gap-4">
        <div><label className="field-label">Full name *</label><input className={inputCls("name")} value={values.name} onChange={set("name")} data-testid="partner-name-input" />{err("name")}</div>
        <div><label className="field-label">Work email *</label><input type="email" className={inputCls("email")} value={values.email} onChange={set("email")} data-testid="partner-email-input" />{err("email")}</div>
      </div>
      <div className="grid sm:grid-cols-2 gap-4">
        <div><label className="field-label">Phone *</label><input className={inputCls("phone")} value={values.phone} onChange={set("phone")} data-testid="partner-phone-input" />{err("phone")}</div>
        <div><label className="field-label">Company / organisation *</label><input className={inputCls("company")} value={values.company} onChange={set("company")} data-testid="partner-company-input" />{err("company")}</div>
      </div>
      <div className="grid sm:grid-cols-2 gap-4">
        <div>
          <label className="field-label">Primary market *</label>
          <select className={inputCls("market")} value={values.market} onChange={set("market")} data-testid="partner-market-select">
            <option value="">Select…</option>
            {MARKETS.map((m) => <option key={m}>{m}</option>)}
          </select>
          {err("market")}
        </div>
        <div>
          <label className="field-label">Monthly requirement *</label>
          <select className={inputCls("monthly_requirement")} value={values.monthly_requirement} onChange={set("monthly_requirement")} data-testid="partner-requirement-select">
            <option value="">Select…</option>
            {REQUIREMENTS.map((m) => <option key={m}>{m}</option>)}
          </select>
          {err("monthly_requirement")}
        </div>
      </div>
      <div className="grid sm:grid-cols-2 gap-4">
        {category.qualification.map((f) => (
          <div key={f.name}>
            <label className="field-label">{f.label} *</label>
            {f.type === "select" ? (
              <select className={inputCls(f.name)} value={qual[f.name] || ""} onChange={(e) => setQual({ ...qual, [f.name]: e.target.value })} data-testid={`partner-qual-${f.name}`}>
                <option value="">Select…</option>
                {f.options.map((o) => <option key={o}>{o}</option>)}
              </select>
            ) : (
              <input className={inputCls(f.name)} value={qual[f.name] || ""} onChange={(e) => setQual({ ...qual, [f.name]: e.target.value })} data-testid={`partner-qual-${f.name}`} />
            )}
            {err(f.name)}
          </div>
        ))}
      </div>
      <div>
        <label className="field-label">Tell us about the opportunity * <span className="normal-case text-[#8a978f]">({values.message.trim().length}/1,500 — min 20)</span></label>
        <textarea className={`${inputCls("message")} min-h-[110px]`} value={values.message} onChange={set("message")} data-testid="partner-message-input" />
        {err("message")}
      </div>
      <label className="flex items-start gap-2 text-xs text-[#57685e] cursor-pointer">
        <input type="checkbox" checked={values.consent} onChange={set("consent")} className="mt-0.5" data-testid="partner-consent-checkbox" />
        <span>I agree that Europcar may contact me about this enquiry and process my details per the <Link to="/privacy" className="underline">Privacy Policy</Link> ({POLICY_VERSION}). *</span>
      </label>
      {err("consent")}
      {failed && (
        <div className="bg-red-50 border border-red-200 rounded p-3 text-xs text-red-700 flex gap-2" data-testid="partner-form-error">
          <AlertTriangle className="w-4 h-4 shrink-0" /> {failed} — your details are safe above, just try again.
        </div>
      )}
      <button type="submit" disabled={busy} className="btn-cta rounded px-6 py-3 text-xs flex items-center gap-2 disabled:opacity-50" data-testid="partner-submit-btn">
        {busy && <Loader2 className="w-4 h-4 animate-spin" />} {category.cta}
      </button>
      <p className="text-[10px] text-[#8a978f]">No account needed. No sensitive documents collected at this stage.</p>
    </form>
  );
};
